import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../shared/services/apiClient';
import { BarChart3 } from 'lucide-react';
import { isExternal, isAdmin } from '../../../utils/permissions';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import ResponsiveChart from '../../../shared/ui/ResponsiveChart';
import { queryKeys } from '../../../shared/services/queryKeys';

type DashboardItem = {
  id: number;
  name: string;
  description?: string;
  datasource?: string;
  lastUpdate?: string;
};

type Company = {
  id: number;
  name: string;
  cuit?: string;
  rubro?: string;
  lastUpdate?: string;
  registeredAt?: string;
  createdAt?: string;
};

const ClientDashboard: React.FC<{ currentUser?: any }> = ({ currentUser }) => {
  const resolvedUser = useMemo(() => {
    if (currentUser) return currentUser;
    try { return JSON.parse(localStorage.getItem('currentUser') || 'null'); } catch { return null; }
  }, [currentUser]);

  // Quick log to ensure we know who is rendering this view (runs before access guard)
  if (process.env.NODE_ENV === 'development') {
    console.log('[ClientDashboard] resolvedUser snapshot:', {
      id: resolvedUser?.id,
      role: resolvedUser?.role,
      empresaId: resolvedUser?.empresaId || resolvedUser?.companyId,
      sucursalId: resolvedUser?.sucursalId,
    });
  }

  // admins and external users can view this
  if (!resolvedUser || (!isAdmin(resolvedUser) && !isExternal(resolvedUser))) {
    return (
      <div className="p-8 bg-slate-900 min-h-[300px] rounded">
        <h2 className="text-2xl font-bold text-sky-400 mb-4">Acceso denegado</h2>
        <p className="text-slate-400">No tienes permisos para ver este panel.</p>
      </div>
    );
  }

  const [modalOpen, setModalOpen] = useState(false);
  const [activeDashboard, setActiveDashboard] = useState<DashboardItem | null>(null);
  const [showDebugModal, setShowDebugModal] = useState(false);

  const dashboardsQuery = useQuery({
    queryKey: queryKeys.dashboards,
    queryFn: async () => {
      const res = await api.get('/dashboards');
      return Array.isArray(res.data) ? res.data : res.data?.dashboards ?? [];
    },
    enabled: Boolean(resolvedUser),
    staleTime: 120_000,
  });

  const companiesQuery = useQuery({
    queryKey: queryKeys.companies,
    queryFn: async () => {
      const res = await api.get('/companies');
      return Array.isArray(res.data) ? res.data : res.data?.companies ?? [];
    },
    enabled: Boolean(resolvedUser),
    staleTime: 120_000,
  });

  const companyStatsQuery = useQuery({
    queryKey: ['companyStats', resolvedUser?.empresaId],
    queryFn: async () => {
      const statsRes = await api.get(`/companies/${resolvedUser?.empresaId}`);
      return {
        datasourcesCount: statsRes.data?.datasourcesCount || 0,
        usersCount: statsRes.data?.users?.length || 0,
      };
    },
    enabled: Boolean(resolvedUser?.empresaId),
    staleTime: 120_000,
  });

  const dData = dashboardsQuery.data ?? [];
  const companies = companiesQuery.data ?? [];
  const company = companies.length > 0 ? companies[0] : null;
  const companyStats = companyStatsQuery.data ?? null;
  const loading = dashboardsQuery.isLoading || companiesQuery.isLoading;

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    console.group('[ACCESS DIAGNOSTIC] ClientDashboard.load');
    console.log('resolvedUser (id, role, empresaId, sucursalId):', {
      id: resolvedUser?.id,
      role: resolvedUser?.role,
      empresaId: resolvedUser?.empresaId || resolvedUser?.companyId,
      sucursalId: resolvedUser?.sucursalId,
    });

    const allKeys = Object.keys(localStorage || {});
    const dashboardKeys = allKeys.filter(k => k.startsWith('dashboard_access_'));
    const widgetKeys = allKeys.filter(k => k.startsWith('widget_access_'));
    console.log('localStorage keys - dashboard_access_*:', dashboardKeys);
    console.log('localStorage keys - widget_access_*:', widgetKeys);

    // Print contents for dashboards we received from API
    dData.forEach((dash: any) => {
      try {
        const key = `dashboard_access_${dash.id}`;
        const v = localStorage.getItem(key);
        if (v) {
          let parsed = null;
          try { parsed = JSON.parse(v); } catch(e) { parsed = v; }
          console.log(`localStorage[${key}] =`, parsed);
        } else {
          console.log(`localStorage[dashboard_access_${dash.id}] = <none>`);
        }
      } catch (e) {
        console.warn('Error reading localStorage for dashboard', dash.id, e);
      }
    });
    console.groupEnd();
  }, [dData, resolvedUser]);

  const dashboards = useMemo(() => {
    const resolved = resolvedUser;
    let visible: any[] = [];

    // If API returned zero dashboards, keep visible empty (empty state will show)
    if (!Array.isArray(dData) || dData.length === 0) {
      visible = [];
    } else if (!resolved || !(resolved.empresaId ?? resolved.companyId)) {
      // No user company info -> show all dashboards (safe default)
      visible = dData;
    } else {
      const userEmpresa = String(resolved.empresaId ?? resolved.companyId);
      visible = dData.filter((dash: any) => {
        const dashCompany = dash.empresaId ?? dash.companyId ?? dash.company_id ?? (dash.empresa && dash.empresa.id) ?? null;
        if (!dashCompany) return false;
        return String(dashCompany) === userEmpresa;
      });

      // Fallback to all dashboards if company filter unexpectedly yields no results
      if (visible.length === 0) {
        if (process.env.NODE_ENV === 'development') console.log('[ACCESS-FALLBACK] No dashboards matched empresaId; falling back to show all dashboards for demo.');
        visible = dData;
      }
    }

    return visible;
  }, [dData, resolvedUser]);

  const openPreview = (d: DashboardItem) => {
    setActiveDashboard(d);
    setModalOpen(true);
  };

  const closePreview = () => {
    setModalOpen(false);
    setActiveDashboard(null);
  };

  // Build a small mock timeseries for the preview based on dashboard id
  const previewData = (id: number) => {
    const base = (id % 5) + 5;
    const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
    return months.slice(0, 6).map((m, i) => ({
      month: m,
      value: Math.round((base + Math.sin(i + id) * 3) * (10 + i)),
    }));
  };

  return (
    <div className="bg-slate-900 min-h-screen px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-sky-500/10 p-3">
            <BarChart3 className="text-sky-400 w-6 h-6 sm:w-7 sm:h-7" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-sky-400">
              Panel de Empresa — {resolvedUser.companyName || company?.name}
            </h1>
            <p className="text-sm sm:text-base text-slate-400">Visualización y resumen de tu empresa</p>
          </div>
        </div>
      </div>

      {/* Resumen de Empresa */}
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-5 sm:p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-white">Información de la Empresa</h2>
          <span className="text-xs text-slate-500">Resumen rápido</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="rounded-lg bg-slate-900/60 p-4">
            <div className="text-slate-400 text-xs mb-1">Nombre</div>
            <div className="text-white font-medium">{company?.name || 'N/D'}</div>
          </div>
          <div className="rounded-lg bg-slate-900/60 p-4">
            <div className="text-slate-400 text-xs mb-1">CUIT</div>
            <div className="text-white font-medium">{company?.cuit || 'No especificado'}</div>
          </div>
          <div className="rounded-lg bg-slate-900/60 p-4">
            <div className="text-slate-400 text-xs mb-1">Rubro</div>
            <div className="text-white font-medium">{(company as any)?.rubro || 'No especificado'}</div>
          </div>
          <div className="rounded-lg bg-slate-900/60 p-4">
            <div className="text-slate-400 text-xs mb-1">Fecha de registro</div>
            <div className="text-white font-medium">
              {company?.registeredAt
                ? new Date(company.registeredAt).toLocaleDateString('es-AR')
                : company?.lastUpdate
                  ? new Date(company.lastUpdate).toLocaleDateString('es-AR')
                  : 'N/D'}
            </div>
          </div>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="text-sky-400 text-xs uppercase tracking-wide">Dashboards</div>
          <div className="text-white text-xl sm:text-2xl font-bold">{dashboards.length}</div>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="text-sky-400 text-xs uppercase tracking-wide">Fuentes de Datos</div>
          <div className="text-white text-xl sm:text-2xl font-bold">{companyStats?.datasourcesCount ?? 'N/D'}</div>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="text-sky-400 text-xs uppercase tracking-wide">Usuarios</div>
          <div className="text-white text-xl sm:text-2xl font-bold">{companyStats?.usersCount ?? 'N/D'}</div>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="text-sky-400 text-xs uppercase tracking-wide">Última actualización</div>
          <div className="text-white text-xs sm:text-sm">{company?.lastUpdate ?? company?.registeredAt ?? 'N/D'}</div>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400">Cargando dashboards...</div>
      ) : (
        <>
          {dashboards.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-700 p-8 text-center text-slate-400">
              Aún no tienes dashboards asignados
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboards.map(d => (
                <div key={d.id} className="bg-slate-800 border border-slate-700 rounded-2xl p-5 transition-opacity duration-500 opacity-0 animate-fade-in">
                  <div className="flex flex-col gap-3 h-full">
                    <div>
                      <h3 className="text-white font-semibold text-lg mb-1 line-clamp-2">{d.name}</h3>
                      <p className="text-slate-400 text-sm line-clamp-2">{d.description}</p>
                    </div>
                    <div className="rounded-lg bg-slate-900/70 p-3 text-sm text-slate-200">
                      <span className="text-slate-400">Fuente:</span>{' '}
                      <span className="text-white">{(() => {
                        const ds = (d as any).datasource;
                        if (!ds && ds !== '') return 'N/D';
                        if (typeof ds === 'string') return ds || 'N/D';
                        if (typeof ds === 'object') return ds.name ?? ds.titulo ?? ds.nombre ?? 'N/D';
                        return String(ds);
                      })()}</span>
                    </div>
                    <div className="text-slate-400 text-xs">Última actualización: {d.lastUpdate ?? 'N/D'}</div>
                    <div className="mt-auto flex">
                      <button
                        onClick={() => openPreview(d)}
                        className="w-full bg-sky-500 hover:bg-sky-600 text-white px-3 py-2 rounded-lg"
                      >
                        Ver Dashboard
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Dev-only debug floating button */}
      {process.env.NODE_ENV === 'development' && (
        <>
          <button
            onClick={() => setShowDebugModal(true)}
            className="fixed right-4 bottom-4 bg-amber-500 hover:bg-amber-600 text-black px-3 py-2 rounded shadow-lg z-50"
          >
            DEBUG
          </button>

          {showDebugModal && (
            <div className="fixed inset-0 bg-black/50 z-60 flex items-center justify-center p-4">
              <div className="bg-slate-800 rounded-lg p-6 w-full max-w-3xl border border-slate-700 text-sm text-slate-200 overflow-auto max-h-[80vh]">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-bold">Debug Access Dump</h3>
                  <button onClick={() => setShowDebugModal(false)} className="text-slate-400">Cerrar</button>
                </div>
                <pre className="whitespace-pre-wrap break-words text-xs bg-slate-900 p-3 rounded mb-3">{JSON.stringify({
                  resolvedUser: resolvedUser || null,
                  localStorageKeys: Object.keys(localStorage).filter(k => k.startsWith('dashboard_access_') || k.startsWith('widget_access_')),
                }, null, 2)}</pre>
                <div className="space-y-2">
                  {Object.keys(localStorage).filter(k => k.startsWith('dashboard_access_')).map(k => (
                    <div key={k} className="bg-slate-900 p-2 rounded border border-slate-700">
                      <div className="text-sky-300 font-mono text-xs">{k}</div>
                      <pre className="text-xs mt-1">{localStorage.getItem(k)}</pre>
                    </div>
                  ))}
                  {Object.keys(localStorage).filter(k => k.startsWith('widget_access_')).map(k => (
                    <div key={k} className="bg-slate-900 p-2 rounded border border-slate-700">
                      <div className="text-sky-300 font-mono text-xs">{k}</div>
                      <pre className="text-xs mt-1">{localStorage.getItem(k)}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {/* Modal preview */}
      {modalOpen && activeDashboard && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-white text-xl font-semibold">{activeDashboard.name}</h3>
                <p className="text-slate-400 text-sm">{activeDashboard.description}</p>
              </div>
              <div>
                <button onClick={closePreview} className="text-slate-300 hover:text-white">Cerrar</button>
              </div>
            </div>

            <div className="h-64 bg-slate-800 rounded-lg p-3 border border-slate-700">
              <ResponsiveChart minHeight={256}>
                {({ width, height }) => (
                  <LineChart width={width} height={height} data={previewData(activeDashboard.id)} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                    <XAxis dataKey="month" stroke="#94a3b8" />
                    <YAxis stroke="#94a3b8" />
                    <Tooltip />
                      <Line type="monotone" dataKey="value" stroke="#0ea5e9" strokeWidth={3} dot={{ r: 3 }} />
                  </LineChart>
                )}
              </ResponsiveChart>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDashboard;

// simple fade-in animation utility (Tailwind doesn't ship this by default in some setups)
// you can remove if you prefer framer-motion
// add animation class via stylesheet; if not present, cards will just show normally
